// components/TotalConDivisas.jsx
import React from 'react';
import { useDivisas } from '../contexts/DivisasContext';

const TotalConDivisas = ({ 
  montoUSD, 
  label = 'Total', 
  variante = 'default', // 'default' | 'compact'
  moneda = null, // ✅ "VES" | "BRL" | "NINGUNA" | null
  colorMonto = 'text-[#e83e8c]',
  colorFondo = 'bg-[#1a1a2e]',
  colorTexto = 'text-white',
}) => {
  const { convertir, formatNumero, tieneDivisas } = useDivisas();
  const conversion = convertir(montoUSD);

  // ✅ Determinar modo de visualización
  const sinConversion = moneda === 'NINGUNA';
  const soloVES = moneda === 'VES' && tieneDivisas && conversion.bolivares > 0;
  const soloBRL = moneda === 'BRL' && tieneDivisas && conversion.reales > 0;
  const mostrarTodas = !moneda && tieneDivisas && (conversion.bolivares > 0 || conversion.reales > 0);

  // ✅ Monto principal según la moneda del método de pago
  // Si el método es VES → el principal es Bs, el USD es referencia
  // Si el método es BRL → el principal es R$, el USD es referencia
  // Si no hay moneda específica → el principal es USD
  const montoPrincipal = soloVES
    ? { valor: conversion.bolivares, simbolo: 'Bs', decimales: 2 }
    : soloBRL
      ? { valor: conversion.reales, simbolo: 'R$', decimales: 2 }
      : { valor: montoUSD, simbolo: '$', decimales: 0 };

  // ==================== VARIANTE COMPACT ====================
  if (variante === 'compact') {
    return (
      <div className={`${colorFondo} ${colorTexto} p-3 rounded-lg`}>
        <div className="flex justify-between items-baseline font-bold">
          <span>{label}</span>
          <span className={colorMonto}>
            {montoPrincipal.simbolo} {formatNumero(montoPrincipal.valor, montoPrincipal.decimales)}
          </span>
        </div>

        {/* Referencia secundaria */}
        {soloVES && (
          <div className="mt-2 pt-2 border-t border-white/10 flex justify-between items-center text-xs">
            <span className="text-white/60">Referencia</span>
            <span className="text-white/70">≈ ${formatNumero(montoUSD, 0)} USD</span>
          </div>
        )}
        {soloBRL && (
          <div className="mt-2 pt-2 border-t border-white/10 flex justify-between items-center text-xs">
            <span className="text-white/60">Referencia</span>
            <span className="text-white/70">≈ ${formatNumero(montoUSD, 0)} USD</span>
          </div>
        )}
        {mostrarTodas && (
          <div className="mt-2 pt-2 border-t border-white/10 space-y-1.5">
            {conversion.bolivares > 0 && (
              <div className="flex justify-between items-center text-xs">
                <span className="text-white/70">≈ Bolívares</span>
                <span className="font-bold text-emerald-300 bg-emerald-500/15 px-2 py-0.5 rounded-md border border-emerald-400/20">
                  Bs {formatNumero(conversion.bolivares, 2)}
                </span>
              </div>
            )}
            {conversion.reales > 0 && (
              <div className="flex justify-between items-center text-xs">
                <span className="text-white/70">≈ Reales</span>
                <span className="font-bold text-sky-300 bg-sky-500/15 px-2 py-0.5 rounded-md border border-sky-400/20">
                  R$ {formatNumero(conversion.reales, 2)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ==================== VARIANTE DEFAULT ====================
  return (
    <div className="flex justify-between items-start pt-1">
      <span className="font-semibold text-gray-800">{label}</span>
      <div className="text-right">
        {/* Monto principal destacado */}
        <div className={`text-xl font-bold ${colorMonto}`}>
          {montoPrincipal.simbolo} {formatNumero(montoPrincipal.valor, montoPrincipal.decimales)}
        </div>

        {/* Referencia secundaria */}
        {soloVES && (
          <div className="mt-1.5">
            <div className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Ref.</span>
              <span className="text-xs font-medium text-gray-600">
                ≈ ${formatNumero(montoUSD, 0)} USD
              </span>
            </div>
          </div>
        )}
        {soloBRL && (
          <div className="mt-1.5">
            <div className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Ref.</span>
              <span className="text-xs font-medium text-gray-600">
                ≈ ${formatNumero(montoUSD, 0)} USD
              </span>
            </div>
          </div>
        )}
        {mostrarTodas && (
          <div className="mt-1.5 space-y-1">
            {conversion.bolivares > 0 && (
              <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1">
                <span className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide">Bs</span>
                <span className="text-sm font-bold text-emerald-700">
                  {formatNumero(conversion.bolivares, 2)}
                </span>
              </div>
            )}
            {conversion.reales > 0 && (
              <div className="inline-flex items-center gap-1.5 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1 ml-1">
                <span className="text-[10px] font-semibold text-sky-700 uppercase tracking-wide">R$</span>
                <span className="text-sm font-bold text-sky-700">
                  {formatNumero(conversion.reales, 2)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TotalConDivisas;